/** Bản tiếng Anh của lab — khóa theo id; mảng giữ đúng thứ tự & số phần tử như bản gốc. */
export interface LabText {
  title: string;
  skill: string;
  intro: string;
  task: string;
  takeaway: string;
  steps?: string[];
  buckets?: string[];
  items?: { text: string; why: string }[];
  lines?: string[];
  fix?: string;
  starter?: string;
  solution?: string;
  checks?: { label: string; hint: string }[];
}

const TOOL_CHECKS_EN = [
  { label: "Tool name in snake_case describing an action (e.g. get_order_status)", hint: "Lowercase words joined by underscores, starting with a verb: get_…, search_…" },
  { label: "Detailed description (≥ 150 chars): what it does, when to use / not use it", hint: "The description is the tool's \"prompt\". State what it returns, when to use it, and how it differs from similar tools." },
  { label: "input_schema has type \"object\"", hint: "input_schema is a JSON Schema; the root must be { \"type\": \"object\", \"properties\": {…} }" },
  { label: "Has an order_id parameter of type string", hint: "Add properties.order_id with type \"string\"." },
  { label: "Every parameter has a description (with format/example)", hint: "Describe meaning and format, e.g. \"Order ID in the form ORD-12345\"." },
  { label: "Declares required, and required lists only real parameters", hint: "\"required\": [\"order_id\"]" },
  { label: "additionalProperties: false (so strict: true can be enabled)", hint: "Strict tool use requires additionalProperties: false." },
];

const EXTRACT_CHECKS_EN = [
  { label: "Schema root is an object with properties", hint: "{ \"type\": \"object\", \"properties\": { … } }" },
  { label: "invoice_number and total_amount are required", hint: "Both always appear on an invoice → make them required." },
  { label: "total_amount is a number", hint: "Use type number for money so it can be validated and computed." },
  { label: "po_number allows null (often absent → avoid fabrication)", hint: "\"type\": [\"string\", \"null\"] — and instruct the model to return null when it isn't there." },
  { label: "document_type is an enum that includes \"other\"", hint: "A closed enum forces wrong labels onto unfamiliar documents — add \"other\"." },
  { label: "Has document_type_detail (string, nullable) to describe \"other\"", hint: "The \"other + detail\" pattern keeps structure while staying honest." },
];

const PROMPT_CHECKS_EN = [
  { label: "States the audience / context", hint: "Who will read it? What situation are they in?" },
  { label: "States the goal / desired outcome", hint: "What should this email achieve?" },
  { label: "Specifies the tone", hint: "E.g. empathetic, professional, no blame." },
  { label: "Format / length constraint", hint: "E.g. at most 150 words, 3 paragraphs, with a subject line." },
  { label: "Separates input data with XML tags", hint: "Wrap the order details in <order_info>…</order_info>." },
  { label: "Prompt is detailed enough (≥ 250 chars)", hint: "Good prompts are usually longer than you think — context is key." },
];

export const EN_LABS: Record<string, LabText> = {
  "lab-prompt-rewrite": {
    title: "Rewrite a bad prompt", skill: "Prompting",
    intro: "Original prompt: \"write an apology email to a customer for late delivery\". The result was generic, the wrong tone, and too long.",
    task: "Rewrite it as a complete prompt (you may write in English or Vietnamese). Available facts: customer An Nguyen, order ORD-88123, 3 days late because the warehouse was overloaded, the company offers a 15% discount code (SAVE15).",
    checks: PROMPT_CHECKS_EN,
    solution: `You are a customer care agent for our store.
Write an apology email to a customer about a late delivery.

<order_info>
Customer: An Nguyen
Order: ORD-88123
Delay: 3 days, warehouse overloaded during a sale
Compensation: 15% discount code (SAVE15) for the next order
</order_info>

Goal: the customer feels respected and keeps shopping with us.
Tone: sincere, empathetic, no blame, no clichés.
Format: a subject line + at most 120 words in 3 short paragraphs; end with the discount code and how to reach support.`,
    takeaway: "Context + goal + tone + format + clearly separated data = a good prompt. Claude doesn't know what you don't tell it.",
  },
  "lab-choose-model": {
    title: "Pick the right model for each job", skill: "Model selection",
    intro: "Each model tier has its own strengths in capability, speed and cost.",
    task: "Assign each task to the MOST suitable model tier.",
    buckets: ["Haiku — fast, cheap", "Sonnet — balanced", "Opus — deep reasoning"],
    items: [
      { text: "Classify 2 million support messages into 5 labels", why: "High volume, simple task → optimize cost/speed." },
      { text: "Autocomplete suggestions in a chat box that need ultra-low latency", why: "Latency is the top priority." },
      { text: "Everyday coding agent: fix bugs, write tests in the repo", why: "Balances coding capability with speed/cost." },
      { text: "Customer-support chatbot that uses an order-lookup tool", why: "A common agent task that needs good capability at reasonable cost." },
      { text: "Analyze legal risk across 40 complex contracts", why: "Deep reasoning; accuracy matters more than cost." },
      { text: "Plan a multi-step architecture migration for a large system", why: "Long, complex task that needs multi-step reasoning." },
    ],
    takeaway: "There's no \"best model for everything\". Start from the quality requirement, then optimize cost and latency — and measure with evals.",
  },
  "lab-tool-loop": {
    title: "The lifecycle of one tool call", skill: "Tool use",
    intro: "Your app declares a get_weather tool. The user asks: \"Is it raining in Hanoi today?\"",
    task: "Put the steps in the order they happen.",
    steps: [
      "The app sends messages + tool definitions to the Messages API",
      "Claude returns a tool_use block (name, input) with stop_reason = \"tool_use\"",
      "The app runs get_weather({city: \"Hanoi\"}) in its own code",
      "The app sends a user message containing a tool_result with the matching tool_use_id",
      "Claude uses the result to answer, stop_reason = \"end_turn\"",
    ],
    takeaway: "Claude doesn't run client tools itself — your application executes them and returns the result. This is the foundation of every agent loop.",
  },
  "lab-tool-def": {
    title: "Design a production-grade tool definition", skill: "Tool design",
    intro: "The current tool (in the editor below) has a vague name, a two-word description, and an untyped, undescribed parameter — Claude calls it wrong all the time.",
    task: "Rewrite the definition of a tool that looks up an order's status by order ID so it meets every criterion below.",
    starter: `{
  "name": "order",
  "description": "get order",
  "input_schema": {
    "type": "object",
    "properties": {
      "id": {}
    }
  }
}`,
    checks: TOOL_CHECKS_EN,
    solution: `{
  "name": "get_order_status",
  "description": "Look up the current status of ONE order by order ID: status (pending/shipped/delivered/cancelled), tracking number and estimated delivery date. Use when a customer asks where their order is or when it will arrive. Do NOT use to find orders by customer email — use search_orders_by_email instead.",
  "strict": true,
  "input_schema": {
    "type": "object",
    "properties": {
      "order_id": {
        "type": "string",
        "description": "Order ID in the form ORD-12345 (case-sensitive)"
      }
    },
    "required": ["order_id"],
    "additionalProperties": false
  }
}`,
    takeaway: "A clear name, a detailed description that draws boundaries with other tools, described parameters and a tight schema — the #1 factor in accurate tool use.",
  },
  "lab-cache-bug": {
    title: "Why does the cache never hit?", skill: "Prompt caching",
    intro: "The dashboard shows high cache_creation_input_tokens on EVERY request and cache_read_input_tokens = 0.",
    task: "Select the line(s) causing the problem.",
    lines: [
      "POLICY = open(\"policy.md\").read()          # ~30,000 tokens, static",
      "def ask(question: str):",
      "    system = [{",
      "        \"type\": \"text\",",
      "        \"text\": f\"Current time: {datetime.now()}\\n\\n{POLICY}\",",
      "        \"cache_control\": {\"type\": \"ephemeral\"},",
      "    }]",
      "    return client.messages.create(",
      "        model=MODEL, max_tokens=2048, system=system,",
      "        messages=[{\"role\": \"user\", \"content\": question}],",
      "    )",
    ],
    fix: "Remove datetime.now() from the cached prefix (or move it into the user message, after the breakpoint). The cache matches a byte-exact prefix — a value that changes on every request at the start of the prompt means nothing after it can ever match.",
    takeaway: "Stable content first, volatile content after the last breakpoint. Verify with usage.cache_read_input_tokens.",
  },
  "lab-parallel-results": {
    title: "Returning parallel tool results", skill: "Tool use",
    intro: "Claude just returned TWO tool_use blocks (toolu_A: get_weather Hanoi, toolu_B: get_weather Da Nang). These are the messages the app sends next.",
    task: "Select the wrong line(s).",
    lines: [
      "{ \"role\": \"assistant\", \"content\": [ …tool_use toolu_A…, …tool_use toolu_B… ] },",
      "{ \"role\": \"user\", \"content\": [",
      "  { \"type\": \"tool_result\", \"tool_use_id\": \"toolu_A\", \"content\": \"Rain, 24°C\" }",
      "] },",
      "{ \"role\": \"user\", \"content\": [",
      "  { \"type\": \"tool_result\", \"content\": \"Sunny, 31°C\" }",
      "] }",
    ],
    fix: "Put both tool_results in ONE user message, and every tool_result needs the right tool_use_id (toolu_B is missing). Splitting the messages also gradually teaches Claude to stop calling tools in parallel.",
    takeaway: "N parallel tool_use blocks → N tool_results in a single user message, each matching its tool_use_id.",
  },
  "lab-config-placement": {
    title: "Put Claude Code config in the right place", skill: "Claude Code config",
    intro: "Misplaced configuration is the most common trap in CCAR-F (e.g. team standards placed at user level).",
    task: "Assign each requirement to the most suitable mechanism.",
    buckets: ["~/.claude/CLAUDE.md (personal)", "./CLAUDE.md (project, committed)", ".claude/rules/ with paths", "Hook / permission deny"],
    items: [
      { text: "I like short answers and comments in Vietnamese in every project", why: "Personal preference that applies to every project." },
      { text: "The repo's test & lint commands and the team's naming conventions", why: "Team standards must be committed so everyone gets them." },
      { text: "Conventions that only apply to files in db/migrations/**", why: "Path-scoped rule, loaded only when needed." },
      { text: "Test-writing conventions only for **/*.test.ts", why: "A path-scoped rule keeps context lean." },
      { text: "NEVER read .env files", why: "A hard requirement → a deterministic control, not a prompt." },
      { text: "Block every git push --force", why: "A dangerous action → a PreToolUse hook or a deny rule." },
    ],
    takeaway: "Preferences → user. Team standards → project. Path-specific → rules. Mandatory → hooks/permissions (or managed settings at the org level).",
  },
  "lab-escalation": {
    title: "Escalate or resolve?", skill: "Reliability",
    intro: "A customer-support agent may refund up to $500 under a 30-day policy.",
    task: "Classify each situation.",
    buckets: ["Agent resolves it", "Hand off to a human"],
    items: [
      { text: "A frustrated customer wants a refund for a $40 order that arrived late (within 30 days)", why: "Negative sentiment isn't an escalation trigger; the request is within the agent's authority." },
      { text: "The customer says: \"Let me talk to a real person\"", why: "An explicit customer request is always honored." },
      { text: "A request for a $2,300 refund", why: "Beyond the agent's authority." },
      { text: "A customer wants a refund on a 45-day-old order for health reasons — the policy doesn't cover it", why: "A policy gap / exception needs a human decision." },
      { text: "The customer asks for their order's tracking number", why: "A routine lookup." },
      { text: "The agent tried 3 ways but couldn't verify the account", why: "No progress after several attempts." },
    ],
    takeaway: "Reliable triggers: the customer asks, beyond authority / policy gap, no progress. Don't use sentiment or self-reported confidence.",
  },
  "lab-extraction-schema": {
    title: "A hallucination-resistant invoice extraction schema", skill: "Structured output",
    intro: "The invoice pipeline fabricates PO numbers when the document has none, and forces unfamiliar documents into the wrong label.",
    task: "Write a JSON Schema for the fields: invoice_number, total_amount, po_number, document_type (invoice/receipt/…), document_type_detail.",
    checks: EXTRACT_CHECKS_EN,
    solution: `{
  "type": "object",
  "properties": {
    "invoice_number": { "type": "string", "description": "Invoice number printed on the document" },
    "total_amount": { "type": "number", "description": "Total amount due" },
    "po_number": { "type": ["string", "null"], "description": "PO number; null if not present in the document" },
    "document_type": { "type": "string", "enum": ["invoice", "receipt", "credit_note", "other"] },
    "document_type_detail": { "type": ["string", "null"], "description": "Describes the document type when document_type = other" }
  },
  "required": ["invoice_number", "total_amount", "po_number", "document_type", "document_type_detail"],
  "additionalProperties": false
}`,
    takeaway: "Nullable for fields that may be absent; an enum with \"other\" + detail; correct types for validation. A schema guarantees syntax — you still need semantic validation.",
  },
  "lab-ci-job": {
    title: "A hanging CI review job", skill: "Claude Code CI/CD",
    intro: "A GitHub Actions job that runs Claude Code to review PRs hangs until the 6-hour timeout.",
    task: "Select the line(s) causing the problem.",
    lines: [
      "- name: Claude review",
      "  env:",
      "    ANTHROPIC_API_KEY: ${{ secrets.ANTHROPIC_API_KEY }}",
      "  run: |",
      "    claude \"Review this PR's diff, report only bugs and security issues\"",
      "    gh pr comment $PR --body-file review.md",
    ],
    fix: "Missing -p (headless): claude -p \"…\" --output-format json --allowedTools \"Read,Grep,Glob\". Without -p, Claude Code opens an interactive session and waits for input forever.",
    takeaway: "In CI: -p, structured output, restricted tools, and a review session independent from the generating session.",
  },
  "lab-subagent-brief": {
    title: "Write a delegation prompt for a subagent", skill: "Multi-agent",
    intro: "The coordinator delegates with: \"Find more sources for claim 3\" — the subagent goes off-topic because it can't see the coordinator's history.",
    task: "Rewrite a complete delegation prompt (English or Vietnamese). Claim 3: \"Lithium battery costs fell >80% from 2013 to 2023\".",
    checks: [
      { label: "States the specific claim/task (not just \"claim 3\")", hint: "The subagent doesn't know what \"claim 3\" is — spell it out." },
      { label: "Source criteria (credibility, recency…)", hint: "Which kinds of sources are acceptable? From which year?" },
      { label: "Return format (list, fields, quotes)", hint: "What does the coordinator need to synthesize? URL, date, verbatim quote…" },
      { label: "Scope / limits (quantity, what not to do)", hint: "E.g. 3–5 sources, don't rewrite the report." },
      { label: "What to do if nothing is found / sources conflict", hint: "Report it explicitly instead of returning nothing or silently choosing." },
    ],
    solution: `Task: find evidence to verify the claim "Lithium-ion battery costs fell by more than 80% from 2013 to 2023" for a renewable-energy report.

Source criteria: reports from reputable organizations (BloombergNEF, IEA), peer-reviewed papers or government data; published 2020 or later.

Return: at most 5 sources, each with: title, organization, URL, publication date, a verbatim quote containing the figure, and the $/kWh values for the relevant years.

Limits: only find and cite; do not rewrite the report.
If nothing is found or sources conflict: say so explicitly and list both figures with their sources — do not pick one.`,
    takeaway: "A subagent knows only what you write in the delegation prompt. Goal + context + criteria + return format + how to report problems.",
  },
  "lab-model-upgrade": {
    title: "A safe model-upgrade process", skill: "Lifecycle",
    intro: "A newer, cheaper and stronger model was just released. Your system serves 2 million requests a day.",
    task: "Put the steps in a sensible order.",
    steps: [
      "Read the migration guide: breaking changes to parameters/APIs",
      "Update the code for breaking changes on a separate branch",
      "Run the existing eval suite on the new model and compare against the baseline",
      "Tune prompts / effort based on the eval results",
      "Canary-release to a small slice of traffic and watch business metrics",
      "Ramp up gradually to 100%, keeping a rollback path",
    ],
    takeaway: "Never just swap the model ID and deploy. Evals + gradual rollout + rollback.",
  },
  "lab-governance": {
    title: "Tiering controls on agent actions", skill: "Governance",
    intro: "An internal agent has many tools. Decide the level of control for each action based on risk.",
    task: "Assign each action to the appropriate control level.",
    buckets: ["Automatic (logged)", "Needs human approval", "Forbidden"],
    items: [
      { text: "Read the internal wiki to answer questions", why: "Read-only, low risk." },
      { text: "Create a draft Jira ticket", why: "Easily reversible." },
      { text: "Send an external email on an employee's behalf", why: "Can't be recalled; externally visible." },
      { text: "Delete a customer record in the CRM", why: "Irreversible." },
      { text: "Read a file containing production secrets", why: "No business reason; violates least privilege." },
      { text: "Change the agent's own IAM permissions", why: "Self-escalation of privilege." },
    ],
    takeaway: "Control is proportional to reversibility and blast radius. Enforce with policies/hooks, not prompts.",
  },
};
